// Duo Universal Prompt second factor for Admin logins (no serial number).
// When /login answers with MFARequired, the browser completes Duo and the result is exchanged at /login/duo

export const DUO_CALLBACK_PATH = '/login/duo-callback';

export const INVALID_DUO_LOGIN_MESSAGE =
  'Duo verification failed or took too long. Please sign in again.';
export const DUO_DENIED_MESSAGE = 'Duo did not approve the sign in.';

// Holds a login that is waiting on Duo while the browser is away at the Duo prompt.
// sessionStorage keeps it to this tab, and it is removed as soon as the callback page reads it.

const PENDING_DUO_LOGIN_STORAGE_KEY = 'paygate-pending-duo-login';

export interface PendingDuoLogin {
  preAuthToken: string;
}

export const savePendingDuoLogin = (pending: PendingDuoLogin): void => {
  try {
    window.sessionStorage.setItem(PENDING_DUO_LOGIN_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Ignore storage write failures. The callback page will ask the user to sign in again.
  }
};

export const takePendingDuoLogin = (): PendingDuoLogin | null => {
  try {
    const value = window.sessionStorage.getItem(PENDING_DUO_LOGIN_STORAGE_KEY);
    window.sessionStorage.removeItem(PENDING_DUO_LOGIN_STORAGE_KEY);

    if (!value) {
      return null;
    }

    const pending = JSON.parse(value) as Partial<PendingDuoLogin>;
    if (typeof pending.preAuthToken !== 'string' || !pending.preAuthToken) {
      return null;
    }

    return { preAuthToken: pending.preAuthToken };
  } catch {
    return null;
  }
};

// Popup flow: the Duo prompt opens in a popup and the login page waits for its result.
// Duo can sever window.opener on the way back, so the popup and the login page talk over a
// same-origin BroadcastChannel instead. The callback page posts what Duo returned, and the
// login page that owns the matching state answers so the popup knows it can close.

const DUO_CHANNEL_NAME = 'paygate-duo-login';
const POPUP_WIDTH = 480;
const POPUP_HEIGHT = 680;

export interface DuoCallbackResult {
  state?: string;
  duoCode?: string;
  error?: string;
  errorDescription?: string;
}

type DuoChannelMessage =
  | { type: 'callback'; result: DuoCallbackResult }
  | { type: 'ack'; state: string };

const openDuoChannel = (): BroadcastChannel | null =>
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(DUO_CHANNEL_NAME);

// Returns null when the browser blocked the popup, so the caller can redirect instead
export const openDuoPopup = (authUrl: string): Window | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
  const popup = window.open(
    authUrl,
    'paygate-duo-login',
    `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
  );

  if (!popup) {
    return null;
  }
  popup.focus();
  return popup;
};

// The pre-auth token carries the Duo state, which is how this login's popup result is recognised
export const getPreAuthState = (preAuthToken: string): string | undefined => {
  try {
    const payload = preAuthToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(window.atob(payload)) as { state?: unknown };
    return typeof claims.state === 'string' && claims.state ? claims.state : undefined;
  } catch {
    return undefined;
  }
};

// Matches the lifetime of the pre-auth token, after which the login can no longer complete
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

export class DuoPopupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuoPopupError';
  }
}

export interface DuoPopupWait {
  result: Promise<DuoCallbackResult>;
  cancel: () => void;
}

// Waits for the callback page in the popup to report the Duo result for this login's state
export const waitForDuoPopup = (state: string): DuoPopupWait => {
  const channel = openDuoChannel();
  let cancel = () => {};

  const result = new Promise<DuoCallbackResult>((resolve, reject) => {
    if (!channel) {
      reject(new DuoPopupError('Duo sign in is not supported in this browser.'));
      return;
    }

    const finish = () => {
      window.clearTimeout(timer);
      channel.close();
    };

    const timer = window.setTimeout(() => {
      finish();
      reject(new DuoPopupError('Duo verification took too long. Please sign in again.'));
    }, POPUP_TIMEOUT_MS);

    channel.onmessage = (event: MessageEvent<DuoChannelMessage>) => {
      const message = event.data;
      if (message.type !== 'callback' || message.result.state !== state) {
        return;
      }
      channel.postMessage({ type: 'ack', state } as DuoChannelMessage);
      finish();
      resolve(message.result);
    };

    cancel = () => {
      finish();
      reject(new DuoPopupError('Duo sign in was cancelled.'));
    };
  });

  return { result, cancel };
};

const ACK_TIMEOUT_MS = 1500;

// Called by the callback page when it has no pending redirect login. Resolves true when a login
// page in another window took the result, in which case this window is a popup and should close
export const handOffDuoCallback = (result: DuoCallbackResult): Promise<boolean> => {
  const channel = openDuoChannel();
  const state = result.state;

  if (!channel || !state) {
    channel?.close();
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      channel.close();
      resolve(false);
    }, ACK_TIMEOUT_MS);

    channel.onmessage = (event: MessageEvent<DuoChannelMessage>) => {
      const message = event.data;
      if (message.type === 'ack' && message.state === state) {
        window.clearTimeout(timer);
        channel.close();
        resolve(true);
      }
    };

    channel.postMessage({ type: 'callback', result } as DuoChannelMessage);
  });
};
